import { useEffect, useState, useRef, useMemo } from 'react'
import trim from 'lodash/trim'
import uniq from 'lodash/uniq'
import nanoid from 'nanoid'
import { Resume } from '@rio/types'
import { useQuery } from '@tanstack/react-query'
import jwtService from 'builder/services/JwtService'
import { useDebouncedEffect } from 'builder/hooks/useDebouncedEffect'

interface Options {
  locale: string
  position: Resume['position']
  currentSkillNames: string[]
}

// Hook to load skill suggestions from the ML-based service
export const useSuggestions = ({ locale, position, currentSkillNames }: Options): string[] => {
  const lastRequestId = useRef<string | null>(null)
  const currentSkillsHash = useMemo(
    () => [...currentSkillNames].sort().join(', '),
    [currentSkillNames],
  )
  const [debouncedQueryParams, setDebouncedQueryParams] = useState([
    locale,
    position,
    currentSkillsHash,
  ])

  // Track component mounting state
  const isMounted = useRef(true)
  useEffect(() => {
    return () => {
      isMounted.current = false
    }
  }, [])

  // Communicate with the microservice
  const fetchSuggestions = async () => {
    const requestId = nanoid()
    lastRequestId.current = requestId

    if (!(await jwtService.isAuthorized())) {
      return []
    }

    return jwtService.client
      .post<{ suggested_skills?: string[] }>('https://skills-ml.resume.io/suggest', {
        locale,
        job_title: position,
        current_skills: currentSkillNames,
      })
      .then(response => {
        if (!isMounted.current) return []
        // If previous request took too long, skip it
        if (lastRequestId.current !== requestId) return []
        // Avoid displaying empty skills
        const suggestedSkills = (response.data.suggested_skills || []).map(trim).filter(Boolean)
        return uniq(suggestedSkills)
      })
      .catch(() => {
        // If the request fails, return an empty array
        return []
      })
  }

  const { data: suggestions } = useQuery({
    queryKey: ['resumeSkillsSuggestions', ...debouncedQueryParams],
    queryFn: fetchSuggestions,
    initialData: [],
    // this is important because the data never becomes stale in this case
    initialDataUpdatedAt: 0,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    retry: 5,
    retryDelay: 2000,
    staleTime: Infinity,
  })

  // Fetch new suggestions only if user changed his job title or updated skill names list
  // Debounce the callback to avoid extra requests and save money.
  useDebouncedEffect(
    () => {
      setDebouncedQueryParams([locale, position, currentSkillsHash])
    },
    1000,
    // We have to stringify the skill names list since React compares deps with `===`
    [locale, position, currentSkillsHash],
  )

  return suggestions
}
